"use strict";
/**
 * MRD (Medical Records Department) — the vocabulary the server validates and the screens display, in one place so the two cannot
 * disagree. Pure data and pure functions: nothing here reads a database or the clock.
 *
 * WHY CODES ARE CONSTANTS AND NOT TABLES. A deficiency type, a file purpose or a medico-legal nature changes about once a year and
 * is a statement the hospital makes in words, not data an operator edits at the counter. They are validated at the application
 * layer (like `role_modules.module_code`), stored as short VARCHARs, and a hospital that needs another adds one here.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.MRD_CERTIFICATE_KINDS = exports.MRD_AUTHORITY_REQUIRED = exports.MRD_ID_DOCUMENT_CODES = exports.MRD_ID_DOCUMENTS = exports.MRD_ISSUE_KIND_CODES = exports.MRD_ISSUE_KINDS = exports.MRD_RECORD_DOCUMENT_CODES = exports.MRD_RECORD_DOCUMENTS = exports.MRD_REQUEST_STATUSES = exports.MRD_REQUEST_DEFAULT_DUE_DAYS = exports.MRD_REQUEST_DUE_DAYS = exports.MRD_REQUESTER_CODES = exports.MRD_REQUESTER_TYPES = exports.MRD_OUTCOME_TABS = exports.MRD_MANNER_CODES = exports.MRD_MANNERS_OF_DEATH = exports.MRD_SEXES = exports.MRD_BIRTH_OUTCOME_LABEL = exports.MRD_BIRTH_OUTCOMES = exports.MRD_DELIVERY_MODE_CODES = exports.MRD_DELIVERY_MODES = exports.MRD_MLC_NATURE_CODES = exports.MRD_MLC_NATURES = exports.MRD_REGISTER_WINDOW_DAYS = exports.MRD_INBOX_WINDOW_DAYS = exports.MRD_FILE_LOAN_DAYS = exports.MRD_MOVEMENT_STATUSES = exports.MRD_FILE_PURPOSE_CODES = exports.MRD_FILE_PURPOSES = exports.MRD_HOLDER_LABEL = exports.MRD_HOLDER_TYPES = exports.MRD_FILE_KIND_LABEL = exports.MRD_FILE_KINDS = exports.MRD_DEFICIENCY_OVERDUE_DAYS = exports.MRD_DEFICIENCY_STATUSES = exports.MRD_DEFICIENCY_CODES = exports.MRD_DEFICIENCY_ITEMS = exports.MRD_CODING_STATUSES = exports.MRD_CODING_LINES_MAX = exports.MRD_TITLE_MAX = exports.MRD_CODE_MAX = exports.MRD_LINE_ROLE_LABEL = exports.MRD_LINE_ROLES = exports.MRD_PROCEDURE_ROLES = exports.MRD_DIAGNOSIS_ROLES = exports.MRD_CODE_KINDS = exports.MRD_CODE_SYSTEMS = exports.MRD_PROCEDURE_SYSTEM_LABEL = exports.MRD_PROCEDURE_SYSTEMS = exports.MRD_DIAGNOSIS_SYSTEM = void 0;
exports.MRD_ICD11_CHAPTERS = exports.MRD_SCAN_KIND_CODES = exports.MRD_SCAN_KINDS = exports.MRD_DESTRUCTION_BLOCKERS = exports.MRD_BATCH_MAX_FILES = exports.MRD_DESTRUCTION_METHODS = exports.MRD_BATCH_STATUSES = exports.MRD_RETENTION_YEARS_MAX = exports.MRD_RECORD_CLASS_CODES = exports.MRD_RECORD_CLASSES = exports.MRD_BOX_STATUSES = exports.MRD_CERTIFICATE_KIND_CODES = void 0;
exports.kindOfRole = kindOfRole;
exports.codingProblems = codingProblems;
exports.deathCertificateProblems = deathCertificateProblems;
exports.birthCertificateProblems = birthCertificateProblems;
exports.requestDueDays = requestDueDays;
exports.requestVerifyProblems = requestVerifyProblems;
exports.medicalCertificateProblems = medicalCertificateProblems;
exports.recordClassOf = recordClassOf;
exports.icd11ChapterOf = icd11ChapterOf;
exports.icd11ChapterPrefixes = icd11ChapterPrefixes;
/* ── coding ──────────────────────────────────────────────────────────────── */
/** Diagnoses are coded in ICD-11 MMS (the hospital's decision, 2026-11-02). */
exports.MRD_DIAGNOSIS_SYSTEM = 'ICD11';
/**
 * The code systems a PROCEDURE ("surgical") code can come from.
 *
 * ICD-11 has no procedure part: its MMS classifies diseases only, and WHO's companion for interventions, ICHI, is still in beta.
 * Until the hospital names the one set its insurers and assessors expect, the coder chooses the system per line and the master can
 * hold any of them. `LOCAL` is the hospital's own list.
 */
exports.MRD_PROCEDURE_SYSTEMS = ['ICHI', 'SNOMED_CT', 'ICD10PCS', 'CPT', 'HBP', 'LOCAL'];
exports.MRD_PROCEDURE_SYSTEM_LABEL = {
    ICHI: 'ICHI (WHO interventions)',
    SNOMED_CT: 'SNOMED CT',
    ICD10PCS: 'ICD-10-PCS',
    CPT: 'CPT',
    HBP: 'PM-JAY package code',
    LOCAL: 'Hospital list',
};
exports.MRD_CODE_SYSTEMS = [exports.MRD_DIAGNOSIS_SYSTEM, ...exports.MRD_PROCEDURE_SYSTEMS];
exports.MRD_CODE_KINDS = ['DIAGNOSIS', 'PROCEDURE'];
exports.MRD_DIAGNOSIS_ROLES = ['PRINCIPAL', 'SECONDARY', 'COMORBIDITY', 'COMPLICATION', 'EXTERNAL_CAUSE'];
exports.MRD_PROCEDURE_ROLES = ['PRINCIPAL_PROCEDURE', 'SECONDARY_PROCEDURE'];
exports.MRD_LINE_ROLES = [...exports.MRD_DIAGNOSIS_ROLES, ...exports.MRD_PROCEDURE_ROLES];
exports.MRD_LINE_ROLE_LABEL = {
    PRINCIPAL: 'Principal diagnosis',
    SECONDARY: 'Secondary diagnosis',
    COMORBIDITY: 'Comorbidity',
    COMPLICATION: 'Complication',
    EXTERNAL_CAUSE: 'External cause',
    PRINCIPAL_PROCEDURE: 'Principal procedure',
    SECONDARY_PROCEDURE: 'Other procedure',
};
function kindOfRole(role) {
    return exports.MRD_DIAGNOSIS_ROLES.includes(role) ? 'DIAGNOSIS' : 'PROCEDURE';
}
/** An ICD-11 code that is a CLUSTER (stem and extension codes joined with & or /) is longer than a stem, so the field is generous. */
exports.MRD_CODE_MAX = 120;
exports.MRD_TITLE_MAX = 400;
exports.MRD_CODING_LINES_MAX = 40;
exports.MRD_CODING_STATUSES = ['DRAFT', 'FINAL'];
/** Every problem that stops a coding being FINAL, in the coder's words. Empty = it may be finalised. */
function codingProblems(lines) {
    const problems = [];
    const principal = lines.filter((l) => l.role === 'PRINCIPAL');
    if (principal.length === 0)
        problems.push('Choose a principal diagnosis.');
    if (principal.length > 1)
        problems.push('Only one line can be the principal diagnosis.');
    lines.forEach((l, i) => {
        const n = i + 1;
        if (!l.code.trim())
            problems.push(`Line ${n}: enter the code.`);
        if (!l.title.trim())
            problems.push(`Line ${n}: enter what the code means.`);
        const kind = kindOfRole(l.role);
        if (kind === 'DIAGNOSIS' && l.codeSystem !== exports.MRD_DIAGNOSIS_SYSTEM)
            problems.push(`Line ${n}: a diagnosis is coded in ICD-11.`);
        if (kind === 'PROCEDURE' && !exports.MRD_PROCEDURE_SYSTEMS.includes(l.codeSystem)) {
            problems.push(`Line ${n}: choose the code system of this procedure.`);
        }
    });
    const seen = new Set();
    for (const l of lines) {
        const key = `${l.codeSystem}|${l.code.trim().toUpperCase()}|${kindOfRole(l.role)}`;
        if (l.code.trim() && seen.has(key))
            problems.push(`${l.code.trim()} is entered twice.`);
        seen.add(key);
    }
    return problems;
}
/* ── completeness / deficiency ───────────────────────────────────────────── */
exports.MRD_DEFICIENCY_ITEMS = [
    { code: 'DISCHARGE_SUMMARY_MISSING', label: 'Discharge summary not written', owner: 'Treating doctor' },
    { code: 'DISCHARGE_SUMMARY_UNSIGNED', label: 'Discharge summary not signed', owner: 'Treating doctor' },
    { code: 'FINAL_DIAGNOSIS_MISSING', label: 'Final diagnosis not stated', owner: 'Treating doctor' },
    { code: 'ADMISSION_NOTE_MISSING', label: 'Admission note / assessment missing', owner: 'Admitting doctor' },
    { code: 'PROGRESS_NOTES_INCOMPLETE', label: 'Progress notes incomplete', owner: 'Treating doctor' },
    { code: 'DOCTOR_SIGNATURE_MISSING', label: 'Doctor’s signature missing', owner: 'Treating doctor' },
    { code: 'CONSENT_MISSING', label: 'Consent form missing or unsigned', owner: 'Ward / doctor' },
    { code: 'OT_NOTES_MISSING', label: 'Operation notes missing', owner: 'Surgeon' },
    { code: 'ANAESTHESIA_RECORD_MISSING', label: 'Anaesthesia record missing', owner: 'Anaesthetist' },
    { code: 'NURSING_CHART_MISSING', label: 'Nursing chart / TPR chart missing', owner: 'Ward nursing' },
    { code: 'INVESTIGATION_REPORTS_MISSING', label: 'Investigation reports not filed', owner: 'Ward' },
    { code: 'MEDICATION_CHART_INCOMPLETE', label: 'Medication chart incomplete', owner: 'Ward nursing' },
    { code: 'MLC_PAPERS_MISSING', label: 'Medico-legal papers missing', owner: 'Casualty / doctor' },
    { code: 'DEATH_SUMMARY_MISSING', label: 'Death summary / cause of death not written', owner: 'Treating doctor' },
    { code: 'OTHER', label: 'Other (see note)', owner: 'Ward' },
];
exports.MRD_DEFICIENCY_CODES = exports.MRD_DEFICIENCY_ITEMS.map((i) => i.code);
exports.MRD_DEFICIENCY_STATUSES = ['OPEN', 'RESOLVED', 'WAIVED'];
/** A deficiency open this many days or more is "overdue" — NABH-style chart-completion expectation, a number the desk reads. */
exports.MRD_DEFICIENCY_OVERDUE_DAYS = 7;
/* ── the physical file ───────────────────────────────────────────────────── */
exports.MRD_FILE_KINDS = ['PAPER', 'ELECTRONIC', 'BOTH'];
exports.MRD_FILE_KIND_LABEL = { PAPER: 'Paper file', ELECTRONIC: 'Electronic only', BOTH: 'Paper and electronic' };
exports.MRD_HOLDER_TYPES = ['WARD', 'DOCTOR', 'DEPARTMENT', 'INSURER_TPA', 'POLICE_COURT', 'PATIENT_FAMILY', 'OTHER'];
exports.MRD_HOLDER_LABEL = {
    WARD: 'Ward',
    DOCTOR: 'Doctor',
    DEPARTMENT: 'Department',
    INSURER_TPA: 'Insurer / TPA',
    POLICE_COURT: 'Police / court',
    PATIENT_FAMILY: 'Patient / family',
    OTHER: 'Other',
};
exports.MRD_FILE_PURPOSES = [
    { code: 'CLINICAL_REFERENCE', label: 'Clinical reference' },
    { code: 'READMISSION', label: 'Readmission (attach old record)' },
    { code: 'COMPLETE_FILE', label: 'Complete the file (deficiency)' },
    { code: 'DEATH_AUDIT', label: 'Death / case audit' },
    { code: 'MEDICO_LEGAL', label: 'Medico-legal' },
    { code: 'INSURANCE', label: 'Insurance / TPA claim' },
    { code: 'RTI_COURT', label: 'RTI / court' },
    { code: 'RESEARCH', label: 'Research / thesis' },
    { code: 'CORRECTION', label: 'Name / address correction' },
    { code: 'OTHER', label: 'Other' },
];
exports.MRD_FILE_PURPOSE_CODES = exports.MRD_FILE_PURPOSES.map((p) => p.code);
exports.MRD_MOVEMENT_STATUSES = ['OUT', 'RETURNED', 'MISSING'];
/** A file lent out is due back after this many days unless the clerk sets another date. */
exports.MRD_FILE_LOAN_DAYS = 3;
/** The inbox looks at stays that ended in the last this-many days by default. Without a bound it would list every stay ever discharged. */
exports.MRD_INBOX_WINDOW_DAYS = 30;
/**
 * The registers' "awaiting" lists (deaths with no register entry, MLC stays with no register detail) look back this many days. Without a bound
 * the first day of use would list every death the hospital has ever had; back-filling older years is a separate decision, not an accident.
 */
exports.MRD_REGISTER_WINDOW_DAYS = 90;
/* ── registers ───────────────────────────────────────────────────────────── */
exports.MRD_MLC_NATURES = [
    { code: 'INJURY_ASSAULT', label: 'Injury / assault' },
    { code: 'ROAD_TRAFFIC_ACCIDENT', label: 'Road traffic accident' },
    { code: 'POISONING', label: 'Poisoning' },
    { code: 'BURNS', label: 'Burns' },
    { code: 'SUICIDE_ATTEMPT', label: 'Attempted suicide / self-harm' },
    { code: 'SEXUAL_ASSAULT', label: 'Sexual assault' },
    { code: 'FIREARM', label: 'Firearm / stab injury' },
    { code: 'DROWNING_HANGING', label: 'Drowning / hanging' },
    { code: 'BROUGHT_DEAD', label: 'Brought dead' },
    { code: 'OTHER', label: 'Other' },
];
exports.MRD_MLC_NATURE_CODES = exports.MRD_MLC_NATURES.map((n) => n.code);
exports.MRD_DELIVERY_MODES = [
    { code: 'NORMAL', label: 'Normal vaginal' },
    { code: 'ASSISTED', label: 'Assisted (forceps / vacuum)' },
    { code: 'CAESAREAN', label: 'Caesarean section' },
    { code: 'OTHER', label: 'Other' },
];
exports.MRD_DELIVERY_MODE_CODES = exports.MRD_DELIVERY_MODES.map((m) => m.code);
exports.MRD_BIRTH_OUTCOMES = ['LIVE_BIRTH', 'STILLBIRTH'];
exports.MRD_BIRTH_OUTCOME_LABEL = { LIVE_BIRTH: 'Live birth', STILLBIRTH: 'Stillbirth' };
exports.MRD_SEXES = ['MALE', 'FEMALE', 'OTHER'];
exports.MRD_MANNERS_OF_DEATH = [
    { code: 'NATURAL', label: 'Natural' },
    { code: 'ACCIDENT', label: 'Accident' },
    { code: 'SUICIDE', label: 'Suicide' },
    { code: 'HOMICIDE', label: 'Homicide' },
    { code: 'PENDING_INVESTIGATION', label: 'Pending investigation' },
    { code: 'UNDETERMINED', label: 'Could not be determined' },
];
exports.MRD_MANNER_CODES = exports.MRD_MANNERS_OF_DEATH.map((m) => m.code);
/** What a death record needs before a certificate may be printed from it — the cause of death is the whole point of the certificate. */
function deathCertificateProblems(record) {
    const problems = [];
    if (!record.deathAt)
        problems.push('Enter the date and time of death.');
    if (!record.immediateCause?.trim())
        problems.push('Enter the immediate cause of death.');
    if (!record.underlyingCause?.trim())
        problems.push('Enter the underlying cause of death.');
    if (!record.mannerOfDeath)
        problems.push('Choose the manner of death.');
    if (!record.certifyingDoctorName?.trim())
        problems.push('Enter the certifying doctor.');
    return problems;
}
function birthCertificateProblems(record) {
    const problems = [];
    if (!record.birthAt)
        problems.push('Enter the date and time of birth.');
    if (!record.sex)
        problems.push('Choose the baby’s sex.');
    if (!record.outcome)
        problems.push('Choose live birth or stillbirth.');
    if (!record.motherName?.trim())
        problems.push('The mother must be named.');
    return problems;
}
/* ── discharge outcomes shown in the MRD outcome register ────────────────── */
exports.MRD_OUTCOME_TABS = [
    { code: 'DAMA', label: 'DAMA / LAMA' },
    { code: 'TRANSFERRED', label: 'Referred / transferred' },
    { code: 'DECEASED', label: 'Deceased' },
];
/* ── record requests, the issue log, medical certificates ────────────────── */
exports.MRD_REQUESTER_TYPES = [
    { code: 'PATIENT', label: 'The patient' },
    { code: 'RELATIVE', label: 'Relative / attendant' },
    { code: 'INSURER_TPA', label: 'Insurer / TPA' },
    { code: 'POLICE', label: 'Police' },
    { code: 'COURT', label: 'Court' },
    { code: 'RTI', label: 'RTI application' },
    { code: 'LAWYER', label: 'Lawyer' },
    { code: 'DOCTOR_HOSPITAL', label: 'Another doctor / hospital' },
    { code: 'OTHER', label: 'Other' },
];
exports.MRD_REQUESTER_CODES = exports.MRD_REQUESTER_TYPES.map((r) => r.code);
/** Days to answer a request, by who asks. An RTI application has a statutory 30 days; the rest are the desk's own target. */
exports.MRD_REQUEST_DUE_DAYS = { RTI: 30, COURT: 15 };
exports.MRD_REQUEST_DEFAULT_DUE_DAYS = 7;
function requestDueDays(requesterType) {
    return exports.MRD_REQUEST_DUE_DAYS[requesterType] ?? exports.MRD_REQUEST_DEFAULT_DUE_DAYS;
}
exports.MRD_REQUEST_STATUSES = ['RECEIVED', 'VERIFIED', 'ISSUED', 'REJECTED'];
exports.MRD_RECORD_DOCUMENTS = [
    { code: 'DISCHARGE_SUMMARY', label: 'Discharge summary' },
    { code: 'CASE_SHEET', label: 'Case sheet (copy)' },
    { code: 'INVESTIGATION_REPORTS', label: 'Investigation reports' },
    { code: 'FINAL_BILL', label: 'Final bill' },
    { code: 'OT_NOTES', label: 'Operation notes' },
    { code: 'DEATH_SUMMARY', label: 'Death summary' },
    { code: 'MLC_CERTIFICATE', label: 'Wound / injury certificate' },
    { code: 'OTHER_RECORD', label: 'Other record' },
];
exports.MRD_RECORD_DOCUMENT_CODES = exports.MRD_RECORD_DOCUMENTS.map((d) => d.code);
/** Everything the issue log can name: a copy of a record, or one of the certificates MRD prints. */
exports.MRD_ISSUE_KINDS = [
    ...exports.MRD_RECORD_DOCUMENTS,
    { code: 'MEDICAL_CERTIFICATE', label: 'Medical certificate' },
    { code: 'BIRTH_CERTIFICATE', label: 'Birth certificate' },
    { code: 'DEATH_CERTIFICATE', label: 'Death certificate' },
];
exports.MRD_ISSUE_KIND_CODES = exports.MRD_ISSUE_KINDS.map((d) => d.code);
exports.MRD_ID_DOCUMENTS = [
    { code: 'AADHAAR', label: 'Aadhaar' },
    { code: 'VOTER_ID', label: 'Voter ID' },
    { code: 'PASSPORT', label: 'Passport' },
    { code: 'DRIVING_LICENCE', label: 'Driving licence' },
    { code: 'PAN', label: 'PAN card' },
    { code: 'OFFICE_ID', label: 'Office / institution ID' },
    { code: 'OTHER_ID', label: 'Other' },
];
exports.MRD_ID_DOCUMENT_CODES = exports.MRD_ID_DOCUMENTS.map((d) => d.code);
/** Who may be given a record on the strength of who they are, and who must show authority for it. */
exports.MRD_AUTHORITY_REQUIRED = ['RELATIVE', 'INSURER_TPA', 'POLICE', 'COURT', 'RTI', 'LAWYER', 'DOCTOR_HOSPITAL', 'OTHER'];
/** What stops a request being VERIFIED, in the clerk's words. */
function requestVerifyProblems(r) {
    const problems = [];
    if (!r.identityChecked)
        problems.push('Confirm you have seen the requester’s identity.');
    if (!r.identityDocType || !r.identityDocNo?.trim())
        problems.push('Record which identity document was shown, and its number.');
    if (exports.MRD_AUTHORITY_REQUIRED.includes(r.requesterType)) {
        if (!r.authorisationChecked)
            problems.push('Confirm you have seen the authority to receive this record (consent letter, order, application).');
        if (!r.authorisationNote?.trim())
            problems.push('Say what the authority is (letter date, order number, application number).');
    }
    return problems;
}
exports.MRD_CERTIFICATE_KINDS = [
    { code: 'FITNESS', label: 'Fitness certificate' },
    { code: 'SICKNESS', label: 'Sickness / rest certificate' },
    { code: 'ATTENDANCE', label: 'Treatment / attendance certificate' },
];
exports.MRD_CERTIFICATE_KIND_CODES = exports.MRD_CERTIFICATE_KINDS.map((k) => k.code);
function medicalCertificateProblems(c) {
    const problems = [];
    if (!c.kind)
        problems.push('Choose the kind of certificate.');
    if (!c.doctorName?.trim())
        problems.push('Enter the doctor who certifies.');
    if (c.kind && c.kind !== 'FITNESS' && !c.fromDate)
        problems.push('Enter the first day of the period it covers.');
    if (c.fromDate && c.toDate && c.toDate < c.fromDate)
        problems.push('The last day is before the first day.');
    return problems;
}
/* ── archive, retention, destruction ─────────────────────────────────────── */
exports.MRD_BOX_STATUSES = ['OPEN', 'SEALED'];
/** The kinds of record a retention period is set for — and so the only files a destruction batch may name. */
exports.MRD_RECORD_CLASSES = [
    { code: 'IP_CASE_SHEET', label: 'In-patient case sheet' },
    { code: 'MLC_CASE_SHEET', label: 'Medico-legal case sheet' },
    { code: 'DEATH_CASE_SHEET', label: 'Death case sheet' },
];
exports.MRD_RECORD_CLASS_CODES = exports.MRD_RECORD_CLASSES.map((c) => c.code);
exports.MRD_RETENTION_YEARS_MAX = 100;
/** Which retention class a finished stay's file falls under: an MLC is its own class, then a death, else an ordinary stay. */
function recordClassOf(stay) {
    if (stay.isMlc)
        return 'MLC_CASE_SHEET';
    if (stay.outcome === 'DECEASED')
        return 'DEATH_CASE_SHEET';
    return 'IP_CASE_SHEET';
}
exports.MRD_BATCH_STATUSES = ['PROPOSED', 'APPROVED', 'DESTROYED', 'CANCELLED'];
exports.MRD_DESTRUCTION_METHODS = ['SHREDDING', 'INCINERATION', 'PULPING', 'OTHER'];
exports.MRD_BATCH_MAX_FILES = 500;
/** Why a file may NOT be destroyed yet. Each is a fact in the system; none is a judgement. */
exports.MRD_DESTRUCTION_BLOCKERS = {
    NOT_RECEIVED: 'MRD never received this file.',
    OPEN_DEFICIENCY: 'It has an open deficiency.',
    ON_LOAN: 'The file is out on loan.',
    OPEN_REQUEST: 'A record request for it is still open.',
    LEGAL_HOLD: 'A legal hold or protection is active on the patient.',
    ALREADY_DESTROYED: 'It has already been destroyed.',
    IN_BATCH: 'It is already in another open destruction batch.',
    NOT_DUE: 'Its retention period has not ended.',
};
exports.MRD_SCAN_KINDS = [
    { code: 'CASE_SHEET', label: 'Case sheet' },
    { code: 'CONSENT', label: 'Consent form' },
    { code: 'OT_NOTES', label: 'Operation / anaesthesia notes' },
    { code: 'INVESTIGATION', label: 'Investigation report' },
    { code: 'MLC_PAPERS', label: 'Medico-legal papers' },
    { code: 'OTHER_SCAN', label: 'Other' },
];
exports.MRD_SCAN_KIND_CODES = exports.MRD_SCAN_KINDS.map((k) => k.code);
/* ── the ICD-11 chapters (the browsable code list) ──────────────────────────────────────────────────── */
/**
 * The 26 diagnostic chapters of ICD-11 MMS and the two-character code prefixes each one holds, read from the WHO release (2026-01) and checked
 * unambiguous: every prefix belongs to exactly one chapter. The code list does not store a chapter, so a code's chapter is its first two characters.
 * Chapter X (extension codes) and V (functioning assessment) are not diagnoses and are not in the list.
 */
exports.MRD_ICD11_CHAPTERS = [
    { no: '01', title: 'Certain infectious or parasitic diseases', prefixes: ['1A', '1B', '1C', '1D', '1E', '1F', '1G', '1H'] },
    { no: '02', title: 'Neoplasms', prefixes: ['2A', '2B', '2C', '2D', '2E', '2F'] },
    { no: '03', title: 'Diseases of the blood or blood-forming organs', prefixes: ['3A', '3B', '3C'] },
    { no: '04', title: 'Diseases of the immune system', prefixes: ['4A', '4B'] },
    { no: '05', title: 'Endocrine, nutritional or metabolic diseases', prefixes: ['5A', '5B', '5C', '5D'] },
    { no: '06', title: 'Mental, behavioural or neurodevelopmental disorders', prefixes: ['6A', '6B', '6C', '6D', '6E'] },
    { no: '07', title: 'Sleep-wake disorders', prefixes: ['7A', '7B'] },
    { no: '08', title: 'Diseases of the nervous system', prefixes: ['8A', '8B', '8C', '8D', '8E'] },
    { no: '09', title: 'Diseases of the visual system', prefixes: ['9A', '9B', '9C', '9D', '9E'] },
    { no: '10', title: 'Diseases of the ear or mastoid process', prefixes: ['AA', 'AB', 'AC'] },
    { no: '11', title: 'Diseases of the circulatory system', prefixes: ['BA', 'BB', 'BC', 'BD', 'BE'] },
    { no: '12', title: 'Diseases of the respiratory system', prefixes: ['CA', 'CB'] },
    { no: '13', title: 'Diseases of the digestive system', prefixes: ['DA', 'DB', 'DC', 'DD', 'DE'] },
    { no: '14', title: 'Diseases of the skin', prefixes: ['EA', 'EB', 'EC', 'ED', 'EE', 'EF', 'EG', 'EH', 'EJ', 'EK', 'EL', 'EM'] },
    { no: '15', title: 'Diseases of the musculoskeletal system or connective tissue', prefixes: ['FA', 'FB', 'FC'] },
    { no: '16', title: 'Diseases of the genitourinary system', prefixes: ['GA', 'GB', 'GC'] },
    { no: '17', title: 'Conditions related to sexual health', prefixes: ['HA'] },
    { no: '18', title: 'Pregnancy, childbirth or the puerperium', prefixes: ['JA', 'JB'] },
    { no: '19', title: 'Certain conditions originating in the perinatal period', prefixes: ['KA', 'KB', 'KC', 'KD'] },
    { no: '20', title: 'Developmental anomalies', prefixes: ['LA', 'LB', 'LC', 'LD'] },
    { no: '21', title: 'Symptoms, signs or clinical findings, not elsewhere classified', prefixes: ['MA', 'MB', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH'] },
    { no: '22', title: 'Injury, poisoning or certain other consequences of external causes', prefixes: ['NA', 'NB', 'NC', 'ND', 'NE', 'NF'] },
    { no: '23', title: 'External causes of morbidity or mortality', prefixes: ['PA', 'PB', 'PC', 'PD', 'PE', 'PF', 'PG', 'PH', 'PJ', 'PK', 'PL'] },
    { no: '24', title: 'Factors influencing health status or contact with health services', prefixes: ['QA', 'QB', 'QC', 'QD', 'QE', 'QF'] },
    { no: '25', title: 'Codes for special purposes', prefixes: ['RA'] },
    { no: '26', title: 'Supplementary Chapter Traditional Medicine Conditions', prefixes: ['SA', 'SB', 'SC', 'SD', 'SE', 'SF', 'SG', 'SH', 'SJ', 'SK', 'SL', 'SM', 'SN', 'SP', 'SQ', 'SR', 'SS', 'ST'] },
];
/** The chapter an ICD-11 code belongs to, or null when its first two characters match none (e.g. an extension code). Ask it only of an ICD-11 code: another system's code can start with the same two characters. */
function icd11ChapterOf(code) {
    const prefix = code.trim().slice(0, 2).toUpperCase();
    const chapter = exports.MRD_ICD11_CHAPTERS.find((c) => c.prefixes.includes(prefix));
    return chapter ? { no: chapter.no, title: chapter.title } : null;
}
/** The code prefixes of a chapter (`'11'` -> `['BA', 'BB', ...]`), or none when the chapter number is not one of the 26. */
function icd11ChapterPrefixes(no) {
    return exports.MRD_ICD11_CHAPTERS.find((c) => c.no === no)?.prefixes ?? [];
}
//# sourceMappingURL=mrd.js.map